import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCardSubtitle,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonList,
  IonListHeader,
  IonPage,
  IonRow,
  IonToolbar,
  useIonModal,
  useIonRouter,
  useIonToast,
} from "@ionic/react";
import {
  bookmark,
  bookmarkOutline,
  informationCircleOutline,
} from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router";
import { Chapter } from "../components/Chapter";
import TOCModal from "../components/TOCModal";
import BookmarkStore, { addToBookmarks } from "../store/BookmarkStore";
import styles from "./Subject.module.scss";

import { useStoreState } from "pullstate";
import { getBookmarks } from "../store/Selectors";

const Subject = () => {
  const pageRef = useRef();
  const { state } = useLocation();
  const { id: subjectId } = useParams();
  const [subject, setSubject] = useState([]);
  const [fromSearch, setFromSearch] = useState(false);
  const [fromBookmarks, setFromBookmarks] = useState(false);

  const bookmarks = useStoreState(BookmarkStore, getBookmarks);

  const [showToast] = useIonToast();
  const router = useIonRouter();

  const handleDismissTOCModal = () => {
    hideTOCModal();
  };

  const [showTOCModal, hideTOCModal] = useIonModal(TOCModal, {
    dismiss: handleDismissTOCModal,
    subject,
  });

  useEffect(() => {
    if (state && state.subject) {
      setSubject(state.subject);
    }

    if (state && state.fromSearch) {
      setFromSearch(state.fromSearch);
    }

    if (state && state.fromBookmarks) {
      setFromBookmarks(state.fromBookmarks);
    }
  }, [state]);

  const addBookmark = async () => {
    const added = addToBookmarks(subject);
    showToast({
      message: added
        ? "This subject has been bookmarked!"
        : "This subject has been removed from your bookmarks.",
      duration: 2000,
      color: "main",
    });
  };

  const onChapterClick = ({ examCategory, chapterNumber }) => {
    let id;
    let ch;
    if (examCategory) {
      id = examCategory;
      ch = false;
    } else {
      id = subjectId;
      ch = chapterNumber;
    }

    router.push(`/questions?id=${id}${ch ? `&ch=${ch}` : ""}`);
  };

  return (
    <IonPage ref={pageRef}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              text={
                fromSearch ? "Search" : fromBookmarks ? "Bookmarks" : "Subjects"
              }
              color="main"
            />
          </IonButtons>

          <IonButtons slot="end">
            <IonButton onClick={addBookmark}>
              <IonIcon
                icon={bookmarks.includes(subject) ? bookmark : bookmarkOutline}
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div className={styles.headerImage}>
          <img src={subject.image} alt="main cover" />
          <div
            className={`${styles.headerInfo} animate__animated animate__slideInLeft`}
          >
            <h1>{subject.label}</h1>
            <p>{subject.courseCode}</p>
          </div>
        </div>

        <IonGrid>
          <IonRow className="ion-text-center">
            {subject.units?.length &&
              subject.units.map((unit, index) => (
                <IonCol
                  key={`unit_${index}`}
                  size={12 / subject.units.length}
                  style={{
                    overflow: "hidden",
                  }}
                >
                  <IonCardSubtitle>
                    {unit.text.indexOf(":") !== -1
                      ? unit.text.split(":")[1].trim()
                      : unit.text}
                  </IonCardSubtitle>
                </IonCol>
              ))}
          </IonRow>

          <IonRow className="ion-text-center">
            <IonCol size="12">
              <IonButton
                expand="block"
                color="main"
                onClick={() =>
                  showTOCModal({
                    presentingElement: pageRef.current,
                    cssClass: "customModal",
                  })
                }
              >
                <IonIcon icon={informationCircleOutline} />
                &nbsp; Table Of Contents
              </IonButton>
            </IonCol>
          </IonRow>

          {subject.units && (
            <IonList>
              <IonListHeader>
                Chapters (
                {subject.units.reduce(
                  (acc, unit) => acc + unit.chapters.length,
                  0
                )}
                )
              </IonListHeader>
              {subject.units.map((unit, uIndex) =>
                unit.chapters.map((chapter, index) => {
                  const chapterNumber =
                    subject.units
                      .slice(0, uIndex)
                      .reduce((sum, u) => sum + u.chapters.length, 0) +
                    index +
                    1;
                  return (
                    <Chapter
                      key={`chapter_${uIndex}_${index}`}
                      chapter={chapter}
                      image={subject.image}
                      unit={unit}
                      onClick={() =>
                        onChapterClick({
                          examCategory: chapter.value,
                          chapterNumber,
                        })
                      }
                    />
                  );
                })
              )}
            </IonList>
          )}
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default Subject;

